import { useState, useMemo, useEffect } from "react";
import { AttendanceHeader } from "../../Attendance/AttendanceHeader";
import { AttendanceTable } from "../../Attendance/AttendanceTable";
import { generateMonthsFrom2025 } from "../../../utilities";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import {
  createDoorStepAttendanceApi,
  getDoorStepEmployeeAttendanceDataApi,
  getDoorStepEmployeeAttendanceExportApi,
  getDoorStepEmployeeCheckedAttendanceApi,
  getDoorStepEmployeeListApi,
} from "../../../services/DoorStepEmployeeService";

export default function DoorStepEmployeeAttedance() {
  const userRole = useSelector((state) =>
    state.auth.user?.is_superuser ? "admin" : "user" || "user"
  );
  const [selectedMonth, setSelectedMonth] = useState(
    generateMonthsFrom2025()[0]?.value
  );
  const [param, setParam] = useState({
    page: "1",
    page_size: "50",
    query: "",
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [baseData, setBaseData] = useState([]);
  const [attendanceData, setAttendanceData] = useState(null);
  const [total, setTotal] = useState(0);
  const [editColumn, setEditColumn] = useState([]);
  const [confirmEditMode, setConfirmEditMode] = useState(false);
  const [isMarked, setIsMarked] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const totalEmployees = baseData?.length;
    let presentCount = 0;
    let absentCount = 0;

    const today = new Date().getDate();

    baseData?.forEach((employee) => {
      const records = attendanceData?.[employee?.id] || {};
      const record = records[today];

      if (record?.status) {
        switch (record.status.toLowerCase()) {
          case "present":
            presentCount++;
            break;
          case "absent":
            absentCount++;
            break;
        }
      }
    });

    return {
      totalEmployees,
      presentCount,
      absentCount,
    };
  }, [baseData, attendanceData]);

  const handleStatusChange = (employeeId, date, status, isEditable = false) => {
    setAttendanceData((prev) => ({
      ...prev,
      [employeeId]: {
        ...(prev[employeeId] || {}),
        [date]: { status: status, ...(isEditable ? { isEditable: true } : {}) },
      },
    }));
  };
  const getAttendanceData = () => {
    getDoorStepEmployeeAttendanceDataApi({ year_month: selectedMonth })
      .then((res) => {
        const data = res?.data?.results || [];

        const formatted = data.reduce((acc, item) => {
          const empId = item?.employee_id;
          const dateObj = new Date(item.date);
          const day = dateObj.getDate();

          if (!acc[empId]) acc[empId] = {};
          acc[empId][day] = { status: item.status };

          return acc;
        }, {});

        setAttendanceData(formatted);
        setCurrentMonth(selectedMonth);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const getEmployee = () => {
    setLoading(true);
    getDoorStepEmployeeListApi(param)
      .then((res) => {
        const data = res?.data?.results || [];
        setBaseData(data || []);
        setTotal(res?.data?.count || 0);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => setLoading(false));
  };

  const checkMarked = () => {
    getDoorStepEmployeeCheckedAttendanceApi()
      .then((res) => {
        setIsMarked(res?.data?.attendance_marked || false);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Try again later"
        );
      });
  };

  const handleSave = () => {
    const [monthName, yearStr] = selectedMonth.split(" ");
    const year = parseInt(yearStr, 10);
    const monthIndex = new Date(`${monthName} 1, ${year}`).getMonth();

    const formatDate = (year, monthIndex, day) => {
      const mm = String(monthIndex + 1).padStart(2, "0");
      const dd = String(day).padStart(2, "0");
      return `${year}-${mm}-${dd}`;
    };

    const payload = [];

    Object.entries(attendanceData).forEach(([empId, records]) => {
      Object.entries(records).forEach(([day, record]) => {
        if (record?.status && record?.isEditable) {
          const recordStr = formatDate(year, monthIndex, parseInt(day));

          payload.push({
            employee_id: empId,
            date: recordStr,
            status: record.status,
          });
        }
      });
    });

    doAttendance(payload);
  };

  const doAttendance = (payload) => {
    setCreateLoading(true);
    createDoorStepAttendanceApi(payload)
      .then((res) => {
        setEditColumn([]);
        setConfirmEditMode(false);
        getAttendanceData();
        checkMarked();
        toast.success("Attendance Update Successfully");
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error | "Try again later"
        );
      })
      .finally(() => setCreateLoading(false));
  };

  useEffect(() => {
    const todayDate = new Date();
    const today = todayDate.getDate();
    const currentMonth = todayDate.getMonth(); // 0-11
    const currentYear = todayDate.getFullYear();

    const [monthName, yearStr] = selectedMonth.split(" ");
    const selectedMonthIndex = new Date(
      `${monthName} 1, ${yearStr}`
    ).getMonth();
    const selectedYear = parseInt(yearStr, 10);

    if (selectedMonthIndex === currentMonth && selectedYear === currentYear) {
      setAttendanceData((prev) => {
        const updated = { ...prev };

        baseData.forEach((emp) => {
          if (!updated[emp.id]) {
            updated[emp.id] = {};
          }
          if (!updated[emp.id][today]) {
            updated[emp.id][today] = { status: "PRESENT", isEditable: true };
          }
        });

        return updated;
      });
    }
  }, [baseData, selectedMonth, currentMonth]);

  useEffect(() => {
    getAttendanceData();
  }, [selectedMonth]);
  useEffect(() => {
    getEmployee();
  }, [param]);
  useEffect(() => {
    if (userRole !== "admin") {
      checkMarked();
    }
  }, []);
  const handleExport = () => {
    setExportLoading(true);
    getDoorStepEmployeeAttendanceExportApi({
      year_month: selectedMonth,
      export: true,
    })
      .then((res) => {
        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = "Door-step-employee-attendance-report.xlsx";

        // Append to DOM to make it more consistent
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Try again later"
        );
      })
      .finally(() => {
        setExportLoading(false);
      });
  };
  return (
    <div className="container-fluid p-0">
      <AttendanceHeader
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
        param={param}
        setParam={setParam}
        onExport={handleExport}
        exportLoading={exportLoading}
        {...summaryStats}
      />

      <div className="row">
        <div className="col mb-2">
          <AttendanceTable
            employees={baseData}
            loading={loading}
            createLoading={createLoading}
            param={param}
            setParam={setParam}
            total={total}
            attendanceData={attendanceData}
            setAttendaceData={setAttendanceData}
            onStatusChange={handleStatusChange}
            selectedMonth={selectedMonth}
            onSave={handleSave}
            confirmEditMode={confirmEditMode}
            setConfirmEditMode={setConfirmEditMode}
            editColumn={editColumn}
            setEditColumn={setEditColumn}
            isMarked={isMarked}
            isDoorStep={true}
          />
        </div>
      </div>
    </div>
  );
}
